import { Injectable, Injector, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EMPTY, NEVER, Observable, Subscription, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ActivatedRoute, Event, NavigationEnd, Params, Router } from '@angular/router';

import { NotificationService } from './notification.service';
import { BrowserLocalStorageService } from './browser-local-storage.service';
import { MobileAppTestingService } from './mobile-app-testing.service';
import { AppConfigService } from './app-config.service';
import { TranslateService } from '../translate/translate.service';
import { Api, ApiQueryOption } from '../../../shared/constants/api';
import { IMobileApp } from '../../../shared/interfaces/mobile-app.interface';
import { MobileAppUploadStatus } from '../../../shared/constants/mobile-app';
import { NotificationPosition } from '../models/notification.model';
import { PULLING_INTERVAL } from '../shared/constants';
import { SharedCommonUtility } from '../../../shared/utils/common.utility';
import { ILinkedPropertyData, LinkedPropertyUtility } from '../../../shared/utils/linked-property.utility';
import { NavService } from './navigation/nav.service';
import { NavigationContext } from './navigation/nav-structure';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class MobileAppUploadNotificationService implements OnDestroy {
  private static readonly STORAGE_KEY: string = 'mobileAppUploadPolling';
  private static readonly POLL_INTERVAL_MS: number = PULLING_INTERVAL.SLOW;

  private currentWorkspaceId: string | null;
  private currentDigitalPropertyId: string | null;
  private activePollingDpKey: string | null;
  private pollingSubscription: Subscription | null;
  private subscriptions: Subscription;

  public constructor(
    private notificationService: NotificationService,
    private storage: BrowserLocalStorageService,
    private mobileAppTestingService: MobileAppTestingService,
    private appConfigService: AppConfigService,
    private translateService: TranslateService,
    private domSanitizer: DomSanitizer,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private injector: Injector,
  ) {
    this.currentWorkspaceId = null;
    this.currentDigitalPropertyId = null;
    this.activePollingDpKey = null;
    this.pollingSubscription = null;
    this.subscriptions = new Subscription();
  }

  public async init(): Promise<void> {
    // Only initialize in tenant mode, skip in admin mode
    if (!this.appConfigService.isTenantEnabled()) {
      return Promise.resolve();
    }

    const createSubscription = (resolve: Function): void => {
      let navService: NavService | undefined; // lazy load nav service since it's constructor does subscriptions that don't work well with app initÏ

      const subscription: Subscription = this.userService.isAuthenticated$
        .pipe(
          distinctUntilChanged(),
          switchMap((isAuthenticated: boolean) => {
            if (!isAuthenticated) {
              return NEVER;
            }

            if (SharedCommonUtility.isNullish(navService)) {
              navService = this.injector.get(NavService);
            }

            return this.router.events
              .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
              .pipe(withLatestFrom(navService.currentNavigationContext$, this.activatedRoute.queryParams))
              .pipe(map(([event, context, params]: [Event, NavigationContext, Params]) => [context, params]))
              .pipe(finalize(() => this.stopPolling()));
          }),
        )
        .subscribe(([context, params]: [NavigationContext, Params]): void => {
          this.stopPolling();

          if (!LinkedPropertyUtility.hasLinkedPropertyData(params)) {
            return;
          }

          if (context !== NavigationContext.digitalProperty) {
            return;
          }

          const { digitalPropertyId, workspaceId }: ILinkedPropertyData =
            LinkedPropertyUtility.fromLinkedPropertyQueryParam(params);

          this.currentWorkspaceId = workspaceId;
          this.currentDigitalPropertyId = digitalPropertyId;

          if (this.hasTrackedDp(digitalPropertyId)) {
            this.startPollingIfNeeded(workspaceId, digitalPropertyId);
          }
        });
      this.subscriptions.add(subscription);
      resolve();
    };

    return new Promise(createSubscription);
  }

  public beginTracking(workspaceId: string, digitalPropertyId: string, mobileAppId: string): void {
    const key: string = digitalPropertyId;
    const data: ITrackedUploadsStorage = this.getStorage();
    if (SharedCommonUtility.isNullish(data[key])) {
      data[key] = { workspaceId, digitalPropertyId, apps: [] };
    }

    data[key].apps.push(mobileAppId);
    this.setStorage(data);

    if (this.currentWorkspaceId === workspaceId && this.currentDigitalPropertyId === digitalPropertyId) {
      this.startPollingIfNeeded(workspaceId, digitalPropertyId);
    }
  }

  private startPollingIfNeeded(workspaceId: string, digitalPropertyId: string): void {
    const key: string = digitalPropertyId;
    if (this.activePollingDpKey === key && SharedCommonUtility.notNullish(this.pollingSubscription)) {
      return;
    }

    this.stopPolling();
    this.activePollingDpKey = key;
    this.pollingSubscription = timer(0, MobileAppUploadNotificationService.POLL_INTERVAL_MS)
      .pipe(
        switchMap(
          (): Observable<IMobileApp[]> =>
            this.mobileAppTestingService
              .getMobileApps(workspaceId, digitalPropertyId)
              .pipe(catchError((): Observable<IMobileApp[]> => EMPTY)),
        ),
      )
      .subscribe({
        next: (apps: IMobileApp[]): void => this.handlePollResult(key, apps),
      });
  }

  public stopPolling(): void {
    if (SharedCommonUtility.notNullish(this.pollingSubscription)) {
      this.pollingSubscription.unsubscribe();
    }
    this.pollingSubscription = null;
    this.activePollingDpKey = null;
  }

  public handlePollResult(dpKey: string, apps: IMobileApp[]): void {
    const data: ITrackedUploadsStorage = this.getStorage();
    const tracked: ITrackedDigitalPropertyUploads | undefined = data[dpKey];
    if (SharedCommonUtility.isNullish(tracked)) {
      // nothing to track anymore
      this.stopPolling();
      return;
    }

    const byId: Map<string, IMobileApp> = new Map<string, IMobileApp>(
      apps.map((a: IMobileApp): [string, IMobileApp] => [a.id, a]),
    );

    const remainingApps: string[] = [];
    tracked.apps.forEach((appId: string): void => {
      const app: IMobileApp | undefined = byId.get(appId);
      const status: MobileAppUploadStatus | null = app ? app.uploadStatus : null;

      if (status === MobileAppUploadStatus.UPLOADED) {
        this.notifySuccess(tracked.workspaceId, tracked.digitalPropertyId);
      } else if (status === MobileAppUploadStatus.FAILED) {
        this.notifyFailure();
      } else if (status === MobileAppUploadStatus.PENDING) {
        // Still pending, keep tracking
        remainingApps.push(appId);
      }
      // For EXPIRED or missing apps, silently stop tracking without notification
    });

    tracked.apps = remainingApps;
    data[dpKey] = tracked;

    if (tracked.apps.length === 0) {
      delete data[dpKey];
      this.setStorage(data);
      this.stopPolling();
      return;
    }

    this.setStorage(data);
  }

  private notifySuccess(workspaceId: string, digitalPropertyId: string): void {
    const linkedPropertyString: string = `${ApiQueryOption.linkedPropertyData}=${LinkedPropertyUtility.getLinkedPropertyString(
      digitalPropertyId,
      workspaceId,
    )}`;
    const scansUrl: string = `/${Api.websitesAndApps}/${Api.mobile_scans}?${linkedPropertyString}`;
    const messageString: string = this.translateService.instant('your_app_was_uploaded_successfully', [scansUrl]);
    const message: SafeHtml = this.domSanitizer.bypassSecurityTrustHtml(messageString);
    this.notificationService.success(message, NotificationPosition.Toast, true);
  }

  private notifyFailure(): void {
    const message: string = this.translateService.instant('mobile_app_upload_failed_notification');
    this.notificationService.error(message, NotificationPosition.Toast, true);
  }

  private hasTrackedDp(dpKey: string): boolean {
    const data: ITrackedUploadsStorage = this.getStorage();
    return typeof data[dpKey] !== 'undefined';
  }

  private getStorage(): ITrackedUploadsStorage {
    return this.storage.getItem(MobileAppUploadNotificationService.STORAGE_KEY) ?? {};
  }

  private setStorage(data: ITrackedUploadsStorage): void {
    this.storage.setItem(MobileAppUploadNotificationService.STORAGE_KEY, data);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.stopPolling();
  }
}

interface ITrackedUploadsStorage {
  [dpKey: string]: ITrackedDigitalPropertyUploads;
}

interface ITrackedDigitalPropertyUploads {
  workspaceId: string;
  digitalPropertyId: string;
  apps: string[];
}
